import { type VariantProps, cva } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '../../../lib';

// No default variants: an unset prop inherits from the parent (Card, Alert, muted container…).
const textVariants = cva('', {
  variants: {
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    tone: {
      default: 'text-foreground',
      muted: 'text-muted-foreground',
      primary: 'text-primary',
      destructive: 'text-destructive',
    },
    truncate: {
      true: 'truncate',
    },
  },
});

type TextVariantProps = VariantProps<typeof textVariants>;

type TextProps = React.ComponentProps<'p'> & TextVariantProps & { inline?: boolean };

function Text({ inline, size, weight, tone, truncate, className, ...props }: TextProps) {
  const Component = inline ? 'span' : 'p';
  return (
    <Component
      className={cn(textVariants({ size, weight, tone, truncate }), className)}
      {...props}
    />
  );
}

const headingElements = { 1: 'h1', 2: 'h2', 3: 'h3', 4: 'h4', 5: 'h5', 6: 'h6' } as const;

type TitleProps = React.ComponentProps<'h1'> &
  Omit<TextVariantProps, 'weight'> & { order: keyof typeof headingElements };

function Title({ order, size, tone, truncate, className, ...props }: TitleProps) {
  const Component = headingElements[order];
  return (
    <Component
      className={cn('font-semibold', textVariants({ size, tone, truncate }), className)}
      {...props}
    />
  );
}

export { Text, Title, textVariants };
